/**
 * Lot SR du chantier 0.5.x — règles PURES de la recherche d'objets de la palette ⌘K : quand
 * interroger la passerelle, et où ouvrir chaque objet trouvé (sa page de détail, dans son
 * contexte de projet ou de programme).
 */
import type { ObjetTrouve } from '@/api/rechercheApi'
import { RECHERCHE_LONGUEUR_MAX, RECHERCHE_LONGUEUR_MIN_TEXTE } from '@/api/rechercheApi'
import { lienSprint } from '@/components/sprints/commun'

const IDENTIFIANT = /^(#?\d{1,18}|ANO-?\d{1,18}|ADR-?\d{1,4})$/i

/** Requête normalisée comme la passerelle : contrôles → espaces, espaces répétés réduits. */
export function normaliserRequete(brute: string): string {
  // eslint-disable-next-line no-control-regex
  return brute.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim()
}

/**
 * La requête vaut-elle un appel ? Même règle que `RechercheObjetsService.analyser` : un identifiant
 * (`9`, `#12`, `ANO-3`, `ADR-1`) dès le premier caractère, un texte libre à partir de 2, jamais au-delà
 * de 100. Hors de ces bornes, la palette n'appelle pas (elle dit pourquoi) : aucun 400 provoqué.
 */
export function requeteExploitable(brute: string): boolean {
  const q = normaliserRequete(brute)
  const longueur = [...q].length
  if (longueur === 0 || longueur > RECHERCHE_LONGUEUR_MAX) return false
  if (IDENTIFIANT.test(q)) return true
  return longueur >= RECHERCHE_LONGUEUR_MIN_TEXTE
}

export interface Destination {
  /** Écran de détail (chemin et paramètres propres). */
  chemin: string
  /** Projet à rendre courant avant d'ouvrir (objet d'un projet). */
  projet: string | null
  /** Programme à rendre courant avant d'ouvrir (objet d'un programme). */
  programme: string | null
}

const enc = encodeURIComponent

/** Page de détail de chaque type d'objet. */
export function destination(o: ObjetTrouve): Destination {
  const projet = o.workspace_id
  const dans = (chemin: string): Destination => ({ chemin, projet, programme: null })
  switch (o.type) {
    case 'item': return dans(`/items/${enc(o.id)}`)
    case 'defect': return dans(`/anomalies?anomalie=${enc(o.id)}`)
    case 'test_case': return dans(`/tests/cas/${enc(o.id)}`)
    case 'test_campaign':
      return dans(o.detail === 'recette' ? `/recette?campagne=${enc(o.id)}` : `/tests?campagne=${enc(o.id)}`)
    case 'release': return dans(`/versions?version=${enc(o.id)}`)
    // Un déploiement s'ouvre sur la fiche de SA version (historique des déploiements avec preuve).
    case 'deployment': return dans(`/versions?version=${enc(o.detail ?? '')}`)
    case 'mission': return dans(`/missions?mission=${enc(o.id)}`)
    case 'sprint': return dans(lienSprint(Number(o.id)))
    case 'project': return dans('/projet')
    case 'program': return { chemin: '/programme', projet: null, programme: o.program_id ?? o.id }
    case 'requirement': {
      const code = enc(o.reference ?? '')
      if (o.scope === 'program') return { chemin: `/programme/gouvernance?exigence=${code}`, projet: null, programme: o.program_id }
      if (o.scope === 'organisation') return { chemin: `/organisation?exigence=${code}`, projet: null, programme: null }
      return dans(`/exigences?exigence=${code}`)
    }
    case 'adr': {
      if (o.scope === 'program') return { chemin: `/decisions?adr=${enc(o.id)}`, projet: null, programme: o.program_id }
      if (o.scope === 'tool' || o.scope === 'organisation') {
        return { chemin: `/decisions?adr=${enc(o.id)}&portee=tool`, projet: null, programme: null }
      }
      return dans(`/decisions?adr=${enc(o.id)}`)
    }
  }
}
